import { BoxDimensions } from '@/three/types';

const DIMENSION_REGEXP = /([\d]+(?:[.,]\d+)?)/g;
const DIMENSION_SEPARATOR_REGEXP = /\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?/i;
const THREE_DIMENSION_DISPLAY_REGEXP =
	/(\d+(?:[.,]\d+)?)(\s*[x×]\s*)(\d+(?:[.,]\d+)?)(\s*[x×]\s*)(\d+(?:[.,]\d+)?)/i;
const ENVELOPE_STANDARD_SIZES: Record<string, BoxDimensions> = {
	a5: { width: 200, height: 288, depth: 8 },
	a4: { width: 250, height: 350, depth: 8 },
	a3: { width: 321, height: 455, depth: 8 },
};

const clampDimension = (value: number | undefined, fallback: number) =>
	Number.isFinite(value) && value && value > 0 ? value : fallback;

const deriveHeightFallback = (width: number, depth: number) =>
	Math.max(Math.min(width, depth) * 0.35, 40);

export const formatCartonDimensionDisplay = (
	rawValue?: string | number | null
): string => {
	if (rawValue === null || rawValue === undefined) return '';
	return String(rawValue).replace(
		THREE_DIMENSION_DISPLAY_REGEXP,
		(_match, first, firstSeparator, second, secondSeparator, third) =>
			`${second}${firstSeparator}${first}${secondSeparator}${third}`
	);
};

export const parseDimensionString = (
	rawValue?: string | number | null
): BoxDimensions | null => {
	if (rawValue === null || rawValue === undefined) return null;
	const value = String(rawValue);
	if (!DIMENSION_SEPARATOR_REGEXP.test(value)) return null;
	const matches = value.replace(/,/g, '.').match(DIMENSION_REGEXP);
	if (!matches || matches.length === 0) return null;

	const [first, second, third] = matches.map((part) => Number.parseFloat(part));

	const width = clampDimension(first, 200);
	const depth = clampDimension(second, width * 0.65);
	const height = clampDimension(third, deriveHeightFallback(width, depth));

	return {
		width,
		height,
		depth,
	};
};

export const parseCartonDimensionString = (
	rawValue?: string | number | null
): BoxDimensions | null => {
	if (rawValue === null || rawValue === undefined) return null;
	const value = String(rawValue);
	if (!DIMENSION_SEPARATOR_REGEXP.test(value)) return null;
	const matches = value.replace(/,/g, '.').match(DIMENSION_REGEXP);
	if (!matches || matches.length === 0) return null;

	const [first, second, third] = matches.map((part) => Number.parseFloat(part));

	const depth = clampDimension(first, 200);
	const width = clampDimension(second, depth * 0.65);
	const height = clampDimension(third, deriveHeightFallback(width, depth));

	return {
		width,
		height,
		depth,
	};
};

const extractDisplayedDimensionNumbers = (
	rawValue?: string | number | null
): number[] | null => {
	if (rawValue === null || rawValue === undefined) return null;
	const value = String(rawValue).trim();
	if (!value) return null;
	const matches = value.replace(/,/g, '.').match(DIMENSION_REGEXP);
	if (!matches?.length) return null;
	const numbers = matches.map((part) => Number.parseFloat(part)).filter(Number.isFinite);
	return numbers.length ? numbers : null;
};

/**
 * Sort carton size options by the numbers shown in the label (left → right),
 * like a numeric alphabetical order. No volume or other derived metrics.
 * Options without parseable dimensions keep relative order at the end.
 */
export const sortCartonSizeOptions = <T extends { label: string; value: string | number; displayLabel?: string }>(
	options: T[]
): T[] => {
	if (options.length <= 1) return options;

	const indexed = options.map((option, index) => {
		const key =
			extractDisplayedDimensionNumbers(option.displayLabel) ??
			extractDisplayedDimensionNumbers(formatCartonDimensionDisplay(option.label)) ??
			extractDisplayedDimensionNumbers(formatCartonDimensionDisplay(option.value)) ??
			extractDisplayedDimensionNumbers(option.label) ??
			extractDisplayedDimensionNumbers(option.value);
		return { option, index, key };
	});

	indexed.sort((a, b) => {
		if (a.key && b.key) {
			const length = Math.max(a.key.length, b.key.length);
			for (let i = 0; i < length; i += 1) {
				const aPart = a.key[i] ?? Number.NEGATIVE_INFINITY;
				const bPart = b.key[i] ?? Number.NEGATIVE_INFINITY;
				if (aPart !== bPart) return aPart - bPart;
			}
			return a.index - b.index;
		}
		if (a.key && !b.key) return -1;
		if (!a.key && b.key) return 1;
		return a.index - b.index;
	});

	return indexed.map(({ option }) => option);
};

export const mergeDimensions = (
	primary: BoxDimensions | null,
	fallback: BoxDimensions | null
): BoxDimensions | null => {
	if (primary) return primary;
	if (fallback) return fallback;
	return null;
};

export const parseEnvelopeDimensionString = (
	rawValue?: string | number | null
): BoxDimensions | null => {
	if (rawValue === null || rawValue === undefined) return null;
	const value = String(rawValue);
	const normalized = value
		.trim()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');
	const standardSizeKey = normalized.match(/\ba[345]\b/)?.[0] ?? normalized;
	const standardSize = ENVELOPE_STANDARD_SIZES[standardSizeKey];
	if (standardSize) return standardSize;

	if (!DIMENSION_SEPARATOR_REGEXP.test(value)) return null;
	const matches = value.replace(/,/g, '.').match(DIMENSION_REGEXP);
	if (!matches || matches.length === 0) return null;

	const [first, second, third] = matches.map((part) => Number.parseFloat(part));
	const width = clampDimension(first, 220);
	const height = clampDimension(second, width * 1.4);
	const base = Math.max(width, height);
	const depth = clampDimension(third, clampDimension(Math.round(base * 0.02), 8));

	return {
		width,
		height,
		depth,
	};
};
